import { Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../Icon';
import type { PointOfInterestRecord } from '../../../../lcpn-uploader/types';
import { theme } from '../../../styles/theme';
import { hexToRgba } from '../../../utils/colour-utils';
import { parseTooltipText } from '../../../utils/parse-tooltip-text';
import { CodeBadge } from '../../CodeBadge';
import { getRgb } from '../../PinMap/get-rgb';
import { isEmpty } from '../../../utils/string-utils';

import type { Heading, Section } from './types';

export function usePoiDataTableDefinitions() {
  const { t } = useTranslation();

  const sections: Section<PointOfInterestRecord>[] = [
    {
      title: '',
      backgroundColor: '',
      rows: [
        {
          title: t('programmeRow.programmeDetail.acronym'),
          key: row => (
            <Stack alignItems={'flex-start'} gap={0.5}>
              <CodeBadge
                code={row.id}
                backgroundColor={getRgb(row.design, true)}
                className="comparison-code-badge"
              />
              {row.studyAcronym}
            </Stack>
          ),
          fallback: '—',
          preserveReactNodes: true,
        },
        {
          title: t('programmeRow.programmeDetail.region'),
          key: 'region',
        },
        {
          title: t('programmeRow.programmeDetail.design'),
          key: 'design',
          fallback: '—',
        },
        {
          title: t('programmeRow.programmeDetail.participantsTargeted'),
          key: 'targetParticipants',
        },
        {
          title: t('programmeRow.programmeDetail.participantsScreened'),
          key: 'screenedParticipants',
          fallback: '—',
        },
      ],
    },
    {
      icon: 'FurtherDetails',
      backgroundColor: theme.colors.default,
      title: t('programmeRow.definitionTables.furtherDetail.title'),
      rows: [
        {
          title: t('programmeRow.definitionTables.furtherDetail.duration'),
          key: row => {
            const start = row.start;
            const finish = row.finish;
            if (isEmpty(start)) return '–';

            // Check if finish is empty, hyphen/dash, or doesn't exist
            if (isEmpty(finish)) {
              return `${start}+`;
            }
            return `${start}—${finish}`;
          },
        },
        {
          title: t('programmeRow.definitionTables.furtherDetail.centres'),
          key: 'studyCentersInCountry',
        },
        {
          title: t(
            'programmeRow.definitionTables.furtherDetail.screeningInterval',
          ),
          key: 'screeningInterval',
        },
        {
          title: t('programmeRow.definitionTables.furtherDetail.radiationDose'),
          key: 'radiationDoseReported',
        },
        {
          title: t('programmeRow.definitionTables.furtherDetail.researchFocus'),
          key: 'researchFocus',
        },
      ],
    },
    {
      icon: 'EligibilityChecker',
      title: t('programmeRow.definitionTables.eligibilityChecker.title'),
      backgroundColor: theme.colors.default,
      rows: [
        {
          title: t('programmeRow.definitionTables.eligibilityChecker.ageRange'),
          key: row => `${row.minAge}-${row.maxAge}`,
        },
        {
          title: parseTooltipText(
            t(
              'programmeRow.definitionTables.eligibilityChecker.smokingHistoryPYs',
            ),
          ),
          key: 'packYears',
        },
        {
          title: parseTooltipText(
            t(
              'programmeRow.definitionTables.eligibilityChecker.smokingHistoryYSQ',
            ),
          ),
          key: 'stoppedSmoking',
          format: v => {
            const value = Number.parseInt((v || '') as string);
            if (Number.isNaN(value)) {
              return v;
            }
            return t(
              'programmeRow.definitionTables.eligibilityChecker.yearsSince',
              {
                replace: {
                  count: value,
                },
              },
            );
          },
        },
        {
          title: t(
            'programmeRow.definitionTables.eligibilityChecker.neverSmokers',
          ),
          key: 'smokersNeverEligible',
        },
        {
          title: t(
            'programmeRow.definitionTables.eligibilityChecker.otherRiskFactors',
          ),
          key: 'otherCriteriaSummary',
        },
        {
          title: t(
            'programmeRow.definitionTables.eligibilityChecker.footnotesTitle',
          ),
          key: study =>
            study.minAge
              ?.concat(
                study.maxAge || '',
                study.packYears || '',
                study.stoppedSmoking || '',
              )
              .includes('*')
              ? study.smokingHistory
              : '',
        },
      ],
    },
    {
      icon: 'ParticipantOutcomes',
      title: t('programmeRow.definitionTables.participantOutcomes.title'),
      backgroundColor: theme.colors.default,
      rows: [
        {
          title: t('programmeRow.definitionTables.participantOutcomes.year'),
          key: 'yearsReported',
        },
        {
          title: t('programmeRow.definitionTables.participantOutcomes.uptake'),
          key: 'screeningUptakeRate',
        },
        {
          title: t(
            'programmeRow.definitionTables.participantOutcomes.confirmedCases',
          ),
          key: 'casesPositiveFindings',
        },
        {
          title: t(
            'programmeRow.definitionTables.participantOutcomes.stageCases',
          ),
          key: 'stage1and2',
        },
      ],
      notes: [
        {
          title: t(
            'programmeRow.definitionTables.participantOutcomes.footnotesTitle',
          ),
          body: t(
            'programmeRow.definitionTables.participantOutcomes.footnotesBody',
          ),
        },
      ],
    },
    {
      title: t(
        'programmeRow.definitionTables.participantOutcomesAdditional.title',
      ),
      backgroundColor: '#333740',
      rows: [
        {
          title: t(
            'programmeRow.definitionTables.participantOutcomesAdditional.updated',
          ),
          key: row => (row.updated ? row.updated.toString() : ''),
        },
        {
          title: t(
            'programmeRow.definitionTables.participantOutcomesAdditional.related',
          ),
          key: row => row.relatedEntries,
          fallback: '—',
        },
        {
          title: t(
            'programmeRow.definitionTables.participantOutcomesAdditional.references',
          ),
          typographyVariant: 'p3',

          key: 'references',
        },
      ],
    },
  ];

  const headings: Heading<PointOfInterestRecord>[] = [
    {
      backgroundColor: theme.colors['blue-100'],
      color: 'white',
      typographyVariant: 'h4',
      key: item => (
        <>
          <Stack direction={'row'} alignItems={'flex-start'} spacing={1}>
            <Box className="comparison-header-icon">
              <Icon width={20} height={20} variant={'MapInvertedBlue'} />
            </Box>
            <div>{item.city}</div>
          </Stack>
        </>
      ),
    },
    {
      backgroundColor: theme.colors['blue-100'],
      color: 'white',
      typographyVariant: 'p1',
      key: 'country',
    },
    {
      backgroundColor: theme.colors['green-100'],
      color: 'white',
      typographyVariant: 'p2',
      mergeCells: true,
      key: () => (
        <>
          <div>
            <strong>Comparing studies:</strong>
          </div>
        </>
      ),
    },

    {
      backgroundColor: hexToRgba(theme.colors['green-100'], 0.6),
      color: 'white',
      typographyVariant: 'p1',
      key: item =>
        t(
          `comparisonTable.poi.${
            item.status === 'Active' ? 'active' : 'inactive'
          }`,
        ),
    },
  ];

  return { headings, sections };
}
